/**
 * Sigma-protocol primitives over Ristretto255: Schnorr signatures,
 * Chaum-Pedersen DLEQ proofs, disjunctive 0/1 proof of an ElGamal
 * ciphertext.
 *
 * All Fiat-Shamir challenges hash a fixed-format byte string:
 *
 *   - Each point  → 32 bytes canonical (Ristretto255 encoding)
 *   - Each scalar → 32 bytes little-endian (normalized to [0, q))
 *   - Each variable-length byte string → 4-byte big-endian length
 *     prefix, then the bytes themselves
 *
 * Length-prefixing variable bytes prevents ambiguous concatenations of
 * heterogeneous items. Fixed-width items don't need it.
 *
 * STATUS: M3 (encrypt). Same primitives are reused at M5 by the
 * trustee partial decryption (Chaum-Pedersen) and the verifier.
 */
import { ristretto255 } from "@noble/curves/ed25519";

import type {
  ChaumPedersenProof,
  DisjunctiveProof,
  ElGamalCiphertext,
  RistrettoPoint,
  Scalar,
  SchnorrSignature,
} from "../types";
import {
  GROUP_ORDER,
  concatBytes,
  encodePoint,
  encodeScalar,
  hashToScalar,
  normalizeScalar,
  randomScalar,
} from "./encoding";

// ---------------------------------------------------------------------------
// Fiat-Shamir transcript hash
// ---------------------------------------------------------------------------

type TranscriptItem = RistrettoPoint | Scalar | Uint8Array;

function lengthPrefixed(bytes: Uint8Array): Uint8Array {
  const out = new Uint8Array(4 + bytes.length);
  const view = new DataView(out.buffer);
  view.setUint32(0, bytes.length, false);
  out.set(bytes, 4);
  return out;
}

function transcriptHash(items: ReadonlyArray<TranscriptItem>): Scalar {
  const chunks: Uint8Array[] = [];
  for (const it of items) {
    if (typeof it === "bigint") {
      chunks.push(encodeScalar(normalizeScalar(it)));
    } else if (it instanceof Uint8Array) {
      chunks.push(lengthPrefixed(it));
    } else {
      chunks.push(encodePoint(it));
    }
  }
  return hashToScalar(concatBytes(...chunks));
}

// ---------------------------------------------------------------------------
// Schnorr signature
// ---------------------------------------------------------------------------

/**
 * Sign `msg` with the secret scalar `sk` (public key `pk = G*sk`) using
 * a Schnorr signature on Ristretto255.
 *
 *   k random;  R = G*k;  e = H(R, pk, msg);  s = k + e*sk mod q.
 */
export function schnorrSign(
  sk: Scalar,
  pk: RistrettoPoint,
  msg: Uint8Array,
): SchnorrSignature {
  const k = randomScalar();
  const R = ristretto255.Point.BASE.multiply(k);
  const e = transcriptHash([R, pk, msg]);
  const s = (k + e * normalizeScalar(sk)) % GROUP_ORDER;
  return { R, s };
}

/**
 * Verify a Schnorr signature: `G*s == R + pk*e`.
 */
export function schnorrVerify(
  pk: RistrettoPoint,
  msg: Uint8Array,
  sig: SchnorrSignature,
): boolean {
  const e = transcriptHash([sig.R, pk, msg]);
  const lhs = ristretto255.Point.BASE.multiplyUnsafe(normalizeScalar(sig.s));
  const rhs = sig.R.add(pk.multiplyUnsafe(normalizeScalar(e)));
  return lhs.equals(rhs);
}

// ---------------------------------------------------------------------------
// Chaum-Pedersen DLEQ proof
// ---------------------------------------------------------------------------

/**
 * Prove non-interactively that `B = A*x` AND `D = C*x` for the same
 * witness `x`, without revealing `x`.
 *
 *   k random;  t1 = A*k;  t2 = C*k;
 *   e = H(A, B, C, D, t1, t2, context);
 *   s = k + e*x mod q.
 */
export function chaumPedersenProve(
  x: Scalar,
  A: RistrettoPoint,
  B: RistrettoPoint,
  C: RistrettoPoint,
  D: RistrettoPoint,
  context: Uint8Array,
): ChaumPedersenProof {
  const k = randomScalar();
  const t1 = A.multiply(k);
  const t2 = C.multiply(k);
  const e = transcriptHash([A, B, C, D, t1, t2, context]);
  const s = (k + e * normalizeScalar(x)) % GROUP_ORDER;
  return { t1, t2, s };
}

/**
 * Verify the proof: recompute `e`; check `A*s == t1 + B*e` AND
 * `C*s == t2 + D*e`.
 */
export function chaumPedersenVerify(
  proof: ChaumPedersenProof,
  A: RistrettoPoint,
  B: RistrettoPoint,
  C: RistrettoPoint,
  D: RistrettoPoint,
  context: Uint8Array,
): boolean {
  const e = transcriptHash([A, B, C, D, proof.t1, proof.t2, context]);
  const eN = normalizeScalar(e);
  const sN = normalizeScalar(proof.s);
  const lhs1 = A.multiplyUnsafe(sN);
  const rhs1 = proof.t1.add(B.multiplyUnsafe(eN));
  if (!lhs1.equals(rhs1)) return false;
  const lhs2 = C.multiplyUnsafe(sN);
  const rhs2 = proof.t2.add(D.multiplyUnsafe(eN));
  return lhs2.equals(rhs2);
}

// ---------------------------------------------------------------------------
// Disjunctive 0-or-1 proof on an ElGamal ciphertext
// ---------------------------------------------------------------------------

/**
 * Prove that the ciphertext `(c1, c2) = (G*r, m + h*r)` encrypts
 * `m = identity` (branch 0) OR `m = G` (branch 1) under the public key
 * `h`, without revealing which.
 *
 *   For the TRUE branch t (∈ {0,1}):
 *     k_t random;  t1_t = G*k_t;  t2_t = h*k_t.
 *
 *   For the FALSE branch f (= 1 - t):
 *     e_f, s_f random;  t1_f = G*s_f - c1*e_f;  t2_f = h*s_f - M_f*e_f
 *     where M_f = c2 (f=0)  or  M_f = c2 - G (f=1).
 *
 *   e = H(G, h, c1, c2, t1_0, t2_0, t1_1, t2_1, context);
 *   e_t = (e - e_f) mod q;  s_t = k_t + e_t*r mod q.
 */
export function disjunctiveProve(
  branch: 0 | 1,
  r: Scalar,
  ciphertext: ElGamalCiphertext,
  h: RistrettoPoint,
  context: Uint8Array,
): DisjunctiveProof {
  const G = ristretto255.Point.BASE;
  const { c1, c2 } = ciphertext;
  const c2MinusG = c2.subtract(G);

  const k = randomScalar();
  const eFake = randomScalar();
  const sFake = randomScalar();
  const eFakeN = normalizeScalar(eFake);

  let t1_0: RistrettoPoint;
  let t2_0: RistrettoPoint;
  let t1_1: RistrettoPoint;
  let t2_1: RistrettoPoint;

  if (branch === 0) {
    // TRUE: branch 0 (encrypts identity); SIMULATE branch 1.
    t1_0 = G.multiply(k);
    t2_0 = h.multiply(k);
    t1_1 = G.multiply(sFake).subtract(c1.multiplyUnsafe(eFakeN));
    t2_1 = h.multiply(sFake).subtract(c2MinusG.multiplyUnsafe(eFakeN));
  } else {
    // TRUE: branch 1 (encrypts G); SIMULATE branch 0.
    t1_0 = G.multiply(sFake).subtract(c1.multiplyUnsafe(eFakeN));
    t2_0 = h.multiply(sFake).subtract(c2.multiplyUnsafe(eFakeN));
    t1_1 = G.multiply(k);
    t2_1 = h.multiply(k);
  }

  const eTotal = transcriptHash([
    G, h, c1, c2, t1_0, t2_0, t1_1, t2_1, context,
  ]);
  const eTrue = (eTotal - eFake + GROUP_ORDER) % GROUP_ORDER;
  const sTrue = (k + eTrue * normalizeScalar(r)) % GROUP_ORDER;

  return branch === 0
    ? { t1_0, t2_0, t1_1, t2_1, e0: eTrue, s0: sTrue, e1: eFake, s1: sFake }
    : { t1_0, t2_0, t1_1, t2_1, e0: eFake, s0: sFake, e1: eTrue, s1: sTrue };
}

/**
 * Verify a disjunctive 0/1 proof for `(c1, c2)` under public key `h`:
 *  - `e_0 + e_1 ≡ H(G, h, c1, c2, t1_0, t2_0, t1_1, t2_1, context) (mod q)`
 *  - `G*s_0 == t1_0 + c1*e_0`
 *  - `h*s_0 == t2_0 + c2*e_0`
 *  - `G*s_1 == t1_1 + c1*e_1`
 *  - `h*s_1 == t2_1 + (c2 - G)*e_1`
 */
export function disjunctiveVerify(
  proof: DisjunctiveProof,
  ciphertext: ElGamalCiphertext,
  h: RistrettoPoint,
  context: Uint8Array,
): boolean {
  const G = ristretto255.Point.BASE;
  const { c1, c2 } = ciphertext;
  const c2MinusG = c2.subtract(G);

  const eExpected = transcriptHash([
    G, h, c1, c2, proof.t1_0, proof.t2_0, proof.t1_1, proof.t2_1, context,
  ]);
  const eSum = (normalizeScalar(proof.e0) + normalizeScalar(proof.e1)) % GROUP_ORDER;
  if (eSum !== normalizeScalar(eExpected)) return false;

  const e0N = normalizeScalar(proof.e0);
  const e1N = normalizeScalar(proof.e1);
  const s0N = normalizeScalar(proof.s0);
  const s1N = normalizeScalar(proof.s1);

  if (!G.multiplyUnsafe(s0N).equals(proof.t1_0.add(c1.multiplyUnsafe(e0N)))) return false;
  if (!h.multiplyUnsafe(s0N).equals(proof.t2_0.add(c2.multiplyUnsafe(e0N)))) return false;
  if (!G.multiplyUnsafe(s1N).equals(proof.t1_1.add(c1.multiplyUnsafe(e1N)))) return false;
  if (!h.multiplyUnsafe(s1N).equals(proof.t2_1.add(c2MinusG.multiplyUnsafe(e1N)))) return false;
  return true;
}
